import { useState, useEffect, useRef } from "react";
import { bookContent } from "@/data/bookContent";
import { TableOfContents } from "@/components/TableOfContents";
import { ChapterSection } from "@/components/ChapterSection";
import { BookCover } from "@/components/BookCover";
import { ClosingSection } from "@/components/ClosingSection";
import { PrintContent } from "@/components/PrintContent";
import { usePdfDownload } from "@/hooks/usePdfDownload";
import { BookOpen, X, FileDown } from "lucide-react";

const SECTION_IDS = ["cover", ...bookContent.map((ch) => ch.id), "closing"];

export default function Home() {
  const [activeChapter, setActiveChapter] = useState("cover");
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const { downloadPdf, isGenerating } = usePdfDownload();

  useEffect(() => {
    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: "-20% 0px -60% 0px",
      threshold: 0,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveChapter(entry.target.id);
        }
      });
    }, options);

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el && observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleChapterClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setMobileTocOpen(false);
  };

  return (
    <>
      <div className="min-h-screen bg-background screen-only">
        <aside className="hidden lg:block fixed left-0 top-0 h-screen w-72 border-r border-border bg-card/80 z-40 overflow-hidden">
          <TableOfContents
            activeChapter={activeChapter}
            onChapterClick={handleChapterClick}
          />
        </aside>

        <main className="lg:ml-72">
          <div className="mx-auto max-w-2xl px-6 py-12" id="pdf-content">
            <section id="cover">
              <BookCover />
            </section>

            {bookContent.map((chapter) => (
              <section key={chapter.id} id={chapter.id}>
                <ChapterSection chapter={chapter} />
              </section>
            ))}

            <section id="closing">
              <ClosingSection />
            </section>
          </div>
        </main>

        <div className="fixed bottom-6 left-6 z-[9999] flex flex-col gap-3">
          <button
            onClick={downloadPdf}
            disabled={isGenerating}
            style={{
              backgroundColor: isGenerating ? "#9ca3af" : "oklch(0.65 0.12 50)",
              color: "oklch(0.15 0.02 240)",
              cursor: isGenerating ? "not-allowed" : "pointer",
            }}
            className="rounded-full px-5 py-3 shadow-lg hover:scale-105 transition-transform flex items-center gap-2 font-medium"
            aria-label="PDFダウンロード"
          >
            <FileDown className="w-5 h-5" />
            <span className="text-sm">{isGenerating ? "生成中..." : "PDFダウンロード"}</span>
          </button>

          <button
            onClick={() => setMobileTocOpen(true)}
            className="lg:hidden bg-primary text-primary-foreground rounded-full p-4 shadow-lg hover:scale-105 transition-transform flex items-center gap-2"
            aria-label="目次を開く"
          >
            <BookOpen className="w-5 h-5" />
          </button>
        </div>

        {mobileTocOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setMobileTocOpen(false)}
            />
            <div className="absolute right-0 top-0 h-full w-80 max-w-[90vw] bg-card border-l border-border shadow-2xl flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <span className="font-semibold text-sm text-foreground">目次</span>
                <button
                  onClick={() => setMobileTocOpen(false)}
                  className="p-1 rounded-lg hover:bg-muted transition-colors"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <TableOfContents
                  activeChapter={activeChapter}
                  onChapterClick={handleChapterClick}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <PrintContent />
    </>
  );
}
